import { Promo } from 'services/cwData';
import { LiveEventStream, Media, Video } from 'types/api/media';
import {
  LoginProps,
  LogoutProps,
  MyStuffAddRemoveProps,
  SelectItemProps,
  SelectPromoProps,
  ViewItemProps,
} from 'services/analytics/MParticle';
import { ButtonClickProps } from 'services/analytics/Conviva';
import { getPageId, PageId } from 'types/pageId';
import { AppData } from '@lightningjs/sdk';
import {
  ChannelEngagementPayload,
  ItemCustomAttrs,
} from 'types/analytics/mParticle';
import type { LoginFlow } from 'components/pages/login/layout/AbstractLoginModal';

// Increment to add new error codes!
const NUMBER_OF_CODES = 50;

// Build array type of length N with values [0, ... , N - 1]
type BuildArray<L extends number, T extends any[] = []> = T['length'] extends L
  ? T
  : BuildArray<L, [...T, T['length']]>;

type Code = `${number}${number}${number}${number}`;

type ErrorCodeMap = Record<
  `c${BuildArray<typeof NUMBER_OF_CODES>[number]}`,
  Code
>;

export type ReportableError = {
  error: unknown;
  userFacing: boolean; // if error is visible to the user (either triggers an error modal or causes unexpected behaviour from user interaction)
  code: Code; // arbitrary unique code
};

export const ErrorCode: ErrorCodeMap = ((): ErrorCodeMap => {
  const obj = {} as ErrorCodeMap;

  for (let i = 0; i < NUMBER_OF_CODES; i++) {
    const key = `c${i}`;
    const value = `${i.toString().padStart(4, '0')}` as Code;
    obj[key as keyof ErrorCodeMap] = value;
  }

  return obj;
})();

export const reportNavBarNavigation = (title: string) => {
  window.analytics.mParticle.reportNavBarNavigation(title);
};

export const reportPageView = (pageId: PageId | string, pageName?: string) => {
  window.analytics.conviva.reportPageView(getPageId(pageId));
  window.analytics.mParticle.reportPageView(pageId, pageName);
};

/**
 * Reports show page engagement for series and live events.
 *
 * @param mediaItem The item selected
 * @param season `season` for `Video`, `slug` for `LiveEventStream`
 * @param imageSrc Image source displayed on selected item
 * @param episodeIndex 0-indexed episode index for `Video`, -1 for `LiveEventStream`
 * @param seasonIndex 0-indexed season index for `Video`, -1 for `LiveEventStream`
 */
export const reportShowPageEngagement = (
  mediaItem: Video | LiveEventStream,
  season: string,
  episodeIndex: number,
  seasonIndex: number,
  pageId: PageId,
) => {
  window.analytics.mParticle.reportShowPageEngagement(
    mediaItem,
    season,
    episodeIndex,
    seasonIndex,
    pageId,
  );
};

export const reportBrowsePageEngagement = (
  seriesName: string,
  rowName: string,
  rowPosition: number,
  creativePosition: number,
) => {
  window.analytics.mParticle.reportBrowsePageEngagement(
    seriesName,
    rowName,
    rowPosition,
    creativePosition,
  );
};

export const reportSearchResultClicked = (
  mediaItem: Media,
  rowName: string,
  rowPosition: number,
  creativePosition: number,
  searchTerm: string,
) => {
  window.analytics.mParticle.reportSearchResultClicked(
    mediaItem,
    rowName,
    rowPosition,
    creativePosition,
    searchTerm,
  );
};

export const reportScrollTracking = (quartile: number, pageId: PageId) => {
  window.analytics.mParticle.reportScrollTracking(quartile, pageId);
};

export const reportViewPromotion = (
  props: { promo: Promo; index: number },
  pageId: PageId,
) => {
  window.analytics.mParticle.reportViewPromotion(props, pageId);
};

export const reportPromoSelected = (
  props: SelectPromoProps,
  pageId: PageId,
) => {
  window.analytics.mParticle.reportPromoSelected(props, pageId);
};

export const reportItemSelected = (props: SelectItemProps, pageId: PageId) => {
  window.analytics.mParticle.reportItemSelected(props, pageId);
};

export const reportGetStarted = (pageId: PageId) => {
  window.analytics.mParticle.reportGetStarted(pageId);
};

export const reportLoginSkipped = (pageId: PageId, loginFlow: LoginFlow) => {
  window.analytics.mParticle.reportLoginSkipped(pageId, loginFlow);
};

export const reportLogin = (props: LoginProps) => {
  window.analytics?.mParticle?.reportLogin(props);
};

export const reportLogout = (props: LogoutProps) => {
  window.analytics?.mParticle?.reportLogout(props);
};

export const reportSignUpPassword = (
  email: string,
  marketingConsent: boolean,
  pageId: PageId,
) => {
  window.analytics?.mParticle?.reportSignUpPassword(
    email,
    marketingConsent,
    pageId,
  );
};

export const reportSignUpMagicLink = (
  email: string,
  marketingConsent: boolean,
  pageId: PageId,
) => {
  window.analytics?.mParticle?.reportSignUpMagicLink(
    email,
    marketingConsent,
    pageId,
  );
};

export const reportViewItemList = (
  products: ViewItemProps[],
  props: Partial<ItemCustomAttrs>,
  pageId: PageId,
) => {
  window.analytics.mParticle.reportViewItemList(products, props, pageId);
};

export const reportChannelEngagement = (
  channelName: string,
  option: ChannelEngagementPayload['option_name'],
) => {
  window.analytics?.mParticle?.reportChannelEngagement(channelName, option);
};

export const reportMyStuffAddRemove = (props: MyStuffAddRemoveProps) => {
  window.analytics.mParticle.reportMyStuffAddRemove(props);
};

export const reportButtonClick = (props: ButtonClickProps) => {
  window.analytics.conviva.reportButtonClick(props);
};

export const reportError = (reportableError: ReportableError) => {
  console.error(`Error[${reportableError.code}]`, reportableError.error);

  // Wait until Conviva is initialized and send the errors once all settled.
  if (reportableError.error instanceof Error && window.analytics) {
    const sendToConviva = () => {
      window.analytics.conviva?.reportError(reportableError);
    };

    window.analytics.initializationPromise?.then(sendToConviva, sendToConviva);
  }

  AppData?.device.reportError(reportableError);
};
